/* eslint-disable react/prop-types */
import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, {
  createContext, useContext, useState,
} from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { logOut as dispatchLogout } from 'Helpers/Authentication';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useEventTrackingIdentify from 'EventTracking/useEventTrackingIdentify';
import useCollection from 'Hooks/useCollection';
import useHomeContext from 'Context/HomeContext';
import { redirectToLogin } from 'Helpers/Redirect';
import { setHasLoggedInBefore } from 'Context/UserContext/hasLoggedInBefore';

const defaultValue = null;

export const UserContext = createContext(defaultValue);

export function Provider(props) {
  const {
    children,
  } = props;
  const { logout: apiLogout, getUser: apiGetUser } = useSwaggerlessContext();
  const { homeResource } = useHomeContext();
  const [user, setUser] = useState(null);
  const [agentResource, setAgentResource] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [userResource, setUserResource] = useState(null);
  const { collection: userInformationCollection, setCollection: setUserInformationCollection } = useCollection();

  if (user) {
    setHasLoggedInBefore(true);
  }

  const identify = useEventTrackingIdentify();

  const checkIsAgent = userRes => {
    if (!userRes) {
      return false;
    }

    return !!userRes.relationship(fr.relationships.agent.name)?.link('related').url();
  };

  const handleAgentRefresh = async (userRes) => {
    if (!checkIsAgent(userRes)) {
      return;
    }
    const [updatedAgentResource] = await userRes.relationship(fr.relationships.agent.name)?.link('related').get();
    setAgentResource(updatedAgentResource);
  };

  const updateUserResource = resource => {
    setUser({ id: resource.id(), ...resource.attributes() });
    setUserResource(resource);

    return handleAgentRefresh(resource);
  };

  const handleUserUpdate = response => {
    if (response) {
      // Handle inconsistency of data being returned as a slice or single item from the server
      let data = response.data; // eslint-disable-line prefer-destructuring

      if (!Array.isArray(data)) {
        data = [data];
      }

      const [newUserResponse] = data;

      const resource = fr.resource(newUserResponse);

      return updateUserResource(resource);
    }

    return response;
  };

  const deprecatedSetApiKey = deprecatedApiKey => setApiKey(deprecatedApiKey);

  const refreshAgent = () => {
    return handleAgentRefresh(userResource);
  };

  const isAgent = checkIsAgent(userResource);

  const hasRole = role => userResource?.attr('role') === role;
  const hasSuperUserRole = hasRole('SuperUser');
  const isInvestmentOps = hasRole('InvestmentOps') || hasSuperUserRole;
  const isLeadPlanner = hasRole('LeadPlanner');

  const logout = () => {
    return apiLogout().then(() => {
      setUserResource(null);
      setUser(null);
      setAgentResource(null);
      setApiKey(null);
      dispatchLogout();
    });
  };

  const fetchUserWithSwagger = () => {
    return apiGetUser()
      .then(res => {
        if (res.status === 503) {
          throw new Error('Down for maintenance');
        }

        return handleUserUpdate(res);
      })
      .catch(err => {
        if (err.status === 401) return; // We redirect on 401 so there's no need to display the error

        displayErrorMessage(err, err?.response?.body?.errors);
      });
  };

  React.useEffect(() => {
    const hasToken = new URL(window.location.href).searchParams.has('t');

    if (!hasToken) {
      if (homeResource.relationship(fr.relationships.user.name)) {
        fetchUserWithSwagger();
      } else {
        redirectToLogin();
      }
    }
  }, []);

  React.useEffect(() => {
    const getUserInformation = async () => {
      try {
        const userInfoColl = await userResource.relationship(fr.relationships.userInformation.name)?.link('related').get();
        const [userInfoResource] = userInfoColl;
        identify(userResource.attr('externalId'), {
          'user.enabledMultiFactors': userInfoResource.attr('enabledMultiFactors'),
          'user.hasNextMeetingScheduled': !!userInfoResource.attr('nextMeetingDate'),
          'user.householdId': userInfoResource.attr('householdId'),
          'user.householdStatusId': userInfoResource.attr('statusId'),
          'user.isAgent': checkIsAgent(userResource),
          'user.leadPlannerId': userInfoResource.attr('leadPlannerId'),
          'user.role': userResource.attr('role'),
        });
        setUserInformationCollection(userInfoColl);
      } catch (err) {
        displayErrorMessage(err, 'Failed to retrieve user information collection');
      }
    };
    if (userResource) {
      getUserInformation();
    }
  }, [userResource]);

  return (
    <UserContext.Provider
      value={{
        logout,
        user,
        setUser,
        updateUserResource,
        agentResource,
        apiKey,
        userResource,
        refreshAgent,
        handleUserUpdate,
        deprecatedSetApiKey,
        isAgent,
        isInvestmentOps,
        isLeadPlanner,
        hasSuperUserRole,
        userInformationCollection,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

Provider.propTypes = {
  children: PropTypes.any.isRequired,
};

export const UserContextProvider = Provider;

export function useMaybeUserContext() {
  return useContext(UserContext);
}

function useUserContext() {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be called from a UserContext.Provider');
  }

  return context;
}

export default useUserContext;

export const withUserContextAdapter = Wrapped => props => {
  const userContext = useUserContext();

  return (
    <Wrapped UserContext={{ ...userContext }} {...props} {...userContext} />
  );
};
